"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useAuth } from "@/app/app/context/AuthContext";
import { useEffect, useId, useRef, useState } from "react";
import { Menu, X, Linkedin, LogOut, ChevronDown } from "lucide-react";
import { FUNCIONALIDADES_NAV } from "@/content/navegacionFuncionalidades";

interface User {
    id?: string;
    nombre: string;
    subscriptionStatus: string;
}

// FEAT-03 (Phase 15): "Funcionalidades" is a WAI-ARIA APG Disclosure Navigation
// Menu, not role="menu" — the entries are destinations, not commands, so the
// composite-widget focus management a menu role implies is the wrong pattern
// here (15-RESEARCH.md, resolved against the fetched APG reference
// implementation). It borrows exactly the remember-trigger/Escape/return-focus
// shape from Modal.tsx and the arrow-key-with-wraparound shape from Tabs.tsx —
// never Modal.tsx's Tab-trap branch, since a disclosure menu must let Tab leave.
const ENLACES_FUNCIONALIDADES = [
    ...FUNCIONALIDADES_NAV.map((entrada) => ({
        href: `/funcionalidades/${entrada.slug}`,
        etiqueta: entrada.etiqueta,
        descripcion: entrada.descripcion,
    })),
    { href: '/funcionalidades', etiqueta: 'Ver todas las funcionalidades' },
];

export default function Header() {
    const pathname = usePathname();
    const { user, loading, logout } = useAuth() as { user: any; loading: boolean; logout: () => void };
    const [mounted, setMounted] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [anclaActiva, setAnclaActiva] = useState<string | null>(null);

    const [funcionalidadesAbierto, setFuncionalidadesAbierto] = useState(false);
    const [funcionalidadesMobileAbierto, setFuncionalidadesMobileAbierto] = useState(false);
    const refTriggerDesktop = useRef<HTMLButtonElement>(null);
    const refPanelDesktop = useRef<HTMLUListElement>(null);
    const refsLinkDesktop = useRef<(HTMLAnchorElement | null)[]>([]);
    const refTriggerMobile = useRef<HTMLButtonElement>(null);
    const idPanelDesktop = useId();
    const idPanelMobile = useId();

    useEffect(() => {
        setMounted(true);
    }, []);

    const navLinks = [
        { href: '/#seguridad', label: 'Seguridad' },
        { href: '/#precios', label: 'Precios' },
        { href: '/blog', label: 'Blog' },
        { href: '/sobre-nosotros', label: 'Sobre nosotros' },
        { href: '/contacto', label: 'Contacto' },
    ];

    useEffect(() => {
        if (pathname !== '/') {
            setAnclaActiva(null);
            return;
        }

        // D-04: 'funcionalidades' removed — the anchor it tracked no longer has a
        // nav entry pointing at it (that entry is now the "Funcionalidades"
        // dropdown, which resolves to real /funcionalidades/<slug> routes, not
        // a home-page scroll target). Leaving the entry here while the nav no
        // longer points at it would leave the scroll-spy tracking a section
        // nothing highlights.
        const anclas = ['seguridad', 'precios'];

        const actualizarAnclaActiva = () => {
            const puntoLectura = window.scrollY + window.innerHeight * 0.35;
            let nuevaAnclaActiva: string | null = null;

            for (const id of anclas) {
                const seccion = document.getElementById(id);
                if (!seccion) continue;
                if (seccion.offsetTop <= puntoLectura) {
                    nuevaAnclaActiva = `/#${id}`;
                }
            }

            setAnclaActiva(nuevaAnclaActiva);
        };

        actualizarAnclaActiva();
        window.addEventListener('scroll', actualizarAnclaActiva, { passive: true });
        window.addEventListener('resize', actualizarAnclaActiva);

        return () => {
            window.removeEventListener('scroll', actualizarAnclaActiva);
            window.removeEventListener('resize', actualizarAnclaActiva);
        };
    }, [pathname]);

    // Remember-trigger/Escape/return-focus, borrowed from Modal.tsx:43-92 minus
    // its Tab-interception branch (Modal.tsx:61-79) — a disclosure nav menu must
    // never trap Tab, unlike a modal dialog.
    useEffect(() => {
        if (!funcionalidadesAbierto) return;

        const alPulsarTecla = (evento: KeyboardEvent) => {
            if (evento.key === 'Escape') {
                setFuncionalidadesAbierto(false);
                refTriggerDesktop.current?.focus();
            }
        };

        const alHacerClicFuera = (evento: MouseEvent) => {
            const objetivo = evento.target as Node;
            if (
                refPanelDesktop.current?.contains(objetivo) ||
                refTriggerDesktop.current?.contains(objetivo)
            ) {
                return;
            }
            setFuncionalidadesAbierto(false);
        };

        document.addEventListener('keydown', alPulsarTecla);
        document.addEventListener('mousedown', alHacerClicFuera);
        return () => {
            document.removeEventListener('keydown', alPulsarTecla);
            document.removeEventListener('mousedown', alHacerClicFuera);
        };
    }, [funcionalidadesAbierto]);

    // Mobile accordion's Escape closes only the accordion, not the whole mobile
    // menu — that stays owned by isMobileMenuOpen, per the UI-SPEC's explicit
    // "differs from desktop" note.
    useEffect(() => {
        if (!funcionalidadesMobileAbierto) return;

        const alPulsarTecla = (evento: KeyboardEvent) => {
            if (evento.key === 'Escape') {
                setFuncionalidadesMobileAbierto(false);
                refTriggerMobile.current?.focus();
            }
        };

        document.addEventListener('keydown', alPulsarTecla);
        return () => document.removeEventListener('keydown', alPulsarTecla);
    }, [funcionalidadesMobileAbierto]);

    const alPulsarTeclaTrigger = (evento: React.KeyboardEvent<HTMLButtonElement>) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
            evento.preventDefault();
            setFuncionalidadesAbierto((previo) => !previo);
            return;
        }
        if (evento.key === 'ArrowDown' && funcionalidadesAbierto) {
            evento.preventDefault();
            refsLinkDesktop.current[0]?.focus();
        }
    };

    // Cambio de diseño pedido por el operador el 2026-09-03 (ver checkpoint de
    // 16-02): el panel pasa de una columna vertical de nueve filas a una
    // rejilla de COLUMNAS_PANEL columnas — el alto del panel baja a un tercio
    // sin tocar el max-height/overflow ya blindado, que era el riesgo real
    // que el checkpoint original mandaba revisar. Con 9 capacidades y 3
    // columnas la rejilla cae exacta en 3 filas completas, sin huecos.
    //
    // Las flechas cambian de sentido respecto a la lista vertical original
    // (adaptada de Tabs.tsx:52-68), documentado explícitamente porque el
    // operador pidió decidir y NO asumir que seguían funcionando igual:
    // - ArrowRight/ArrowLeft: recorren TODO el orden de lectura (las nueve
    //   capacidades más el enlace al hub) con vuelta de punta a punta — es
    //   exactamente el comportamiento que ArrowDown/ArrowUp tenían en la
    //   lista de una sola columna, renombrado porque ahora el orden de
    //   lectura es horizontal primero.
    // - ArrowDown/ArrowUp: se mueven verticalmente saltando de a
    //   COLUMNAS_PANEL. Al caer fuera de la rejilla por abajo (cualquier
    //   celda de la última fila) ArrowDown lleva al enlace del hub, que
    //   vive justo debajo a lo ancho del panel; desde el hub, ArrowDown da
    //   la vuelta a la primera capacidad (arriba a la izquierda). ArrowUp
    //   NO pasa por el hub al subir desde la fila superior: da la vuelta
    //   dentro de la rejilla, a la última fila de la misma columna — el hub
    //   sólo se alcanza subiendo, con ArrowUp, desde sí mismo hacia la
    //   última capacidad.
    // Tab sigue sin manejarse aquí — el panel debe dejar que el foco salga
    // por el orden nativo del navegador, nunca atrapado.
    // Debe coincidir con la clase `grid-cols-3` del panel más abajo — Tailwind
    // necesita la clase completa y estática en el código fuente para
    // generarla, así que este número no puede derivarse de esa clase ni
    // viceversa. Si se cambia la cantidad de columnas, cambiar ambos.
    const COLUMNAS_PANEL = 3;

    const alPulsarTeclaEnlacePanel = (evento: React.KeyboardEvent<HTMLAnchorElement>, indice: number) => {
        const total = ENLACES_FUNCIONALIDADES.length;
        const indiceHub = total - 1;
        const filas = Math.ceil(indiceHub / COLUMNAS_PANEL);

        let destino: number | undefined;

        switch (evento.key) {
            case 'ArrowRight':
                destino = (indice + 1) % total;
                break;
            case 'ArrowLeft':
                destino = (indice - 1 + total) % total;
                break;
            case 'ArrowDown':
                if (indice === indiceHub) {
                    destino = 0;
                } else {
                    const candidato = indice + COLUMNAS_PANEL;
                    destino = candidato < indiceHub ? candidato : indiceHub;
                }
                break;
            case 'ArrowUp':
                if (indice === indiceHub) {
                    destino = indiceHub - 1;
                } else if (indice < COLUMNAS_PANEL) {
                    destino = indice + COLUMNAS_PANEL * (filas - 1);
                } else {
                    destino = indice - COLUMNAS_PANEL;
                }
                break;
            case 'Home':
                destino = 0;
                break;
            case 'End':
                destino = indiceHub;
                break;
            default:
                return;
        }

        evento.preventDefault();
        refsLinkDesktop.current[destino]?.focus();
    };

    const isActive = (path: string) => {
        if (path.startsWith('/#')) {
            return pathname === '/' && anclaActiva === path;
        }
        return pathname === path;
    };

    const claseNavLink = (active: boolean) =>
        `text-sm font-display font-bold transition-all px-3 py-2 rounded-xl border-2 whitespace-nowrap ${active
            ? 'bg-accent-blue border-ink shadow-[2px_2px_0px_0px_var(--shadow-color)] text-on-accent-blue -translate-y-[2px]'
            : 'border-transparent text-ink/60 hover:text-ink hover:bg-tint-neutral-strong'
        }`;

    return (
        <header className="fixed top-0 left-0 right-0 z-50 flex justify-center p-4 sm:p-6 pointer-events-none">
            <nav className="w-full max-w-7xl bg-surface-2 border-2 border-black rounded-[24px] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-center justify-between px-6 py-3 pointer-events-auto">
                {/* Logo and Socials */}
                <div className="flex items-center space-x-3 sm:space-x-4">
                    <Link href="/" className="flex items-center" aria-label="TherapyRecap — Inicio">
                        {/* Same wordmark the app shell and auth screens already use.
                            Intrinsic size is the SVG's own 600x126; the height class
                            drives the rendered size and `w-auto` preserves the ratio. */}
                        <Image
                            src="/THERAPY(2).svg"
                            alt="TherapyRecap"
                            width={600}
                            height={126}
                            className="h-8 sm:h-9 w-auto dark:invert"
                            priority
                        />
                    </Link>
                    <a
                        href="https://www.linkedin.com/company/therapyrecap"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ink hover:text-accent-blue transition-colors flex items-center justify-center p-1 sm:p-1.5 rounded-lg hover:bg-tint-neutral-strong"
                        aria-label="LinkedIn"
                    >
                        <Linkedin size={20} className="w-5 h-5 sm:w-6 sm:h-6" />
                    </a>
                </div>

                {/* Desktop Nav */}
                <div className="hidden lg:flex items-center space-x-1 lg:space-x-2">
                    <div className="relative">
                        <button
                            type="button"
                            ref={refTriggerDesktop}
                            aria-expanded={funcionalidadesAbierto}
                            // El panel se monta y desmonta, no se oculta. Anunciar
                            // `aria-controls` mientras esta desmontado deja una referencia
                            // colgante en el estado POR DEFECTO: la tecnologia asistiva
                            // recibe un id que no resuelve a nada. Se anuncia solo cuando el
                            // elemento referenciado existe de verdad.
                            aria-controls={funcionalidadesAbierto ? idPanelDesktop : undefined}
                            onClick={() => setFuncionalidadesAbierto((previo) => !previo)}
                            onKeyDown={alPulsarTeclaTrigger}
                            className={claseNavLink(false)}
                        >
                            Funcionalidades
                            <ChevronDown
                                aria-hidden="true"
                                size={16}
                                className={`inline-block ml-1 align-middle transition-transform duration-200 ${funcionalidadesAbierto ? 'rotate-180' : ''}`}
                            />
                        </button>
                        {funcionalidadesAbierto && (
                            <ul
                                id={idPanelDesktop}
                                ref={refPanelDesktop}
                                // El panel se ancla bajo un header fijo, así que su alto disponible
                                // es el del viewport menos el header. Con la rejilla de
                                // COLUMNAS_PANEL columnas (pedida por el operador el 2026-09-03) el
                                // alto ya baja a un tercio del de la lista vertical original — el
                                // recorte a 1280×720/1024×768 que este límite existía para blindar
                                // deja de ser el punto crítico — pero el límite se mantiene igual
                                // de todos modos como red de seguridad ante descripciones futuras
                                // más largas. `overscroll-contain` evita que el scroll se encadene
                                // a la página al llegar al final del panel.
                                className="absolute top-full mt-ds-2 left-0 min-w-[720px] max-w-[92vw] z-50 bg-surface-2 border-2 border-ink rounded-ds-lg shadow-ds-3 p-ds-4 grid grid-cols-3 gap-x-ds-6 gap-y-ds-2 max-h-[calc(100dvh_-_9rem)] overflow-y-auto overscroll-contain"
                            >
                                {ENLACES_FUNCIONALIDADES.map((enlace, indice) => (
                                    <li
                                        key={enlace.href}
                                        className={
                                            indice === ENLACES_FUNCIONALIDADES.length - 1
                                                ? 'col-span-3 border-t-2 border-ink/10 mt-ds-2 pt-ds-2'
                                                : undefined
                                        }
                                    >
                                        <Link
                                            href={enlace.href}
                                            ref={(el) => { refsLinkDesktop.current[indice] = el; }}
                                            onClick={() => setFuncionalidadesAbierto(false)}
                                            onKeyDown={(evento) => alPulsarTeclaEnlacePanel(evento, indice)}
                                            className="block px-ds-2 py-ds-2 hover:bg-tint-neutral-strong rounded-lg"
                                        >
                                            <span className="block text-ds-200 font-display font-bold text-ink">
                                                {enlace.etiqueta}
                                            </span>
                                            {'descripcion' in enlace && enlace.descripcion && (
                                                <span className="block text-ds-200 font-medium font-sans text-ink/60 mt-ds-1">
                                                    {enlace.descripcion}
                                                </span>
                                            )}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {navLinks.map((link) => {
                        const active = isActive(link.href);
                        return (
                            <Link
                                key={link.href}
                                href={link.href}
                                onClick={() => setFuncionalidadesAbierto(false)}
                                className={claseNavLink(active)}
                            >
                                {link.label}
                            </Link>
                        );
                    })}
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-h-[40px] justify-end">
                        {mounted && !loading && (
                            user ? (
                                <>
                                    <button
                                        onClick={logout}
                                        className="hidden sm:flex text-ink/60 hover:text-danger transition-colors p-2 rounded-full hover:bg-tint-neutral-strong"
                                        aria-label="Cerrar sesión"
                                    >
                                        <LogOut size={20} />
                                    </button>
                                    <Link
                                        href="/app/dashboard/patients"
                                        className="bg-ink text-canvas px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-display font-bold transition-all hover:scale-105 hover:shadow-lg shadow-ink/10 whitespace-nowrap"
                                    >
                                        Dashboard
                                    </Link>
                                </>
                            ) : (
                                <Link
                                    href="/app/register"
                                    className="bg-ink text-canvas px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-display font-bold transition-all hover:scale-105 hover:shadow-lg shadow-ink/10 whitespace-nowrap"
                                >
                                    Probar gratis
                                </Link>
                            )
                        )}
                    </div>

                    <button
                        className="lg:hidden text-ink p-2"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                        aria-expanded={isMobileMenuOpen}
                    >
                        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                // Mismo límite que el panel desktop, por la misma razón: anclado a `top-24` bajo
                // un header fijo, el menú con el acordeón de funcionalidades desplegado suma diez
                // filas y se sale por abajo en un teléfono. Sin cota ni scroll propio las últimas
                // entradas son inalcanzables al tacto (UI-SPEC E4).
                <div className="lg:hidden absolute top-24 left-4 right-4 bg-surface-2 border-2 border-black rounded-[24px] p-4 flex flex-col space-y-2 shadow-[4px_4px_0px_0px_var(--shadow-color)] pointer-events-auto max-h-[calc(100dvh_-_8rem)] overflow-y-auto overscroll-contain">
                    <div>
                        <button
                            type="button"
                            ref={refTriggerMobile}
                            aria-expanded={funcionalidadesMobileAbierto}
                            // Misma razon que en el panel desktop: sin referencia colgante
                            // mientras el sub-panel esta desmontado.
                            aria-controls={funcionalidadesMobileAbierto ? idPanelMobile : undefined}
                            onClick={() => setFuncionalidadesMobileAbierto((previo) => !previo)}
                            onKeyDown={(evento) => {
                                if (evento.key === 'Enter' || evento.key === ' ') {
                                    evento.preventDefault();
                                    setFuncionalidadesMobileAbierto((previo) => !previo);
                                }
                            }}
                            className="w-full flex items-center justify-between px-4 py-3 text-lg font-display font-bold transition-all rounded-xl border-2 border-transparent text-ink/60 hover:bg-tint-neutral hover:text-ink"
                        >
                            Funcionalidades
                            <ChevronDown
                                aria-hidden="true"
                                size={20}
                                className={`transition-transform duration-200 ${funcionalidadesMobileAbierto ? 'rotate-180' : ''}`}
                            />
                        </button>
                        {funcionalidadesMobileAbierto && (
                            <ul id={idPanelMobile} className="pl-ds-4 flex flex-col space-y-1 mt-1">
                                {ENLACES_FUNCIONALIDADES.map((enlace) => (
                                    <li key={enlace.href}>
                                        <Link
                                            href={enlace.href}
                                            onClick={() => {
                                                setIsMobileMenuOpen(false);
                                                setFuncionalidadesMobileAbierto(false);
                                            }}
                                            className="block px-4 py-3 min-h-[44px] flex items-center text-base font-display font-bold transition-all rounded-xl border-2 border-transparent text-ink/60 hover:bg-tint-neutral hover:text-ink"
                                        >
                                            {enlace.etiqueta}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {navLinks.map((link) => {
                        const active = isActive(link.href);
                        return (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={`block px-4 py-3 text-lg font-display font-bold transition-all rounded-xl border-2 ${active
                                    ? 'bg-accent-blue border-ink shadow-[2px_2px_0px_0px_var(--shadow-color)] text-on-accent-blue'
                                    : 'border-transparent text-ink/60 hover:bg-tint-neutral hover:text-ink'
                                    }`}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                {link.label}
                            </Link>
                        );
                    })}

                    {/* Mobile Auth Actions */}
                    {mounted && !loading && (
                        <div className="border-t-2 border-gray-200 pt-3 mt-1 flex flex-col space-y-2">
                            {user ? (
                                <>
                                    <Link
                                        href="/app/dashboard/patients"
                                        className="block px-4 py-3 text-lg font-display font-bold text-center bg-ink text-canvas rounded-xl transition-all hover:scale-[1.02]"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        Ir al Dashboard
                                    </Link>
                                    <button
                                        onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                                        className="flex items-center justify-center gap-2 px-4 py-3 text-lg font-display font-bold text-danger rounded-xl border-2 border-transparent hover:bg-tint-danger transition-all"
                                    >
                                        <LogOut size={20} />
                                        Cerrar sesión
                                    </button>
                                </>
                            ) : (
                                <Link
                                    href="/app/register"
                                    className="block px-4 py-3 text-lg font-display font-bold text-center bg-ink text-canvas rounded-xl transition-all hover:scale-[1.02]"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Probar gratis
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            )}
        </header>
    );
}
